import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  TextField,
  Chip,
} from '@material-ui/core';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon,
} from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 500,
  },
  uploadArea: {
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  fileList: {
    maxHeight: 200,
    overflow: 'auto',
  },
  chipInput: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  successMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.success.main,
    marginTop: theme.spacing(2),
  },
}));

const EXPERTISE_LEVELS = ['beginner', 'intermediate', 'advanced', 'expert'];
const DOCUMENT_PURPOSES = ['reference', 'tutorial', 'regulation', 'methodology', 'best_practices', 'case_study'];
const COMMON_CONTEXTS = [
  'valuation',
  'succession_planning',
  'compliance',
  'marketing',
  'business_continuity',
  'deal_negotiation',
  'client_acquisition',
  'financial_planning',
];

const BulkImportDialog = ({ open, onClose, onSubmit, categories }) => {
  const classes = useStyles();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    tags: [],
    applicableContexts: [],
    industryTags: [],
    expertiseLevel: 'intermediate',
    documentPurpose: 'reference',
  });
  const [tagInput, setTagInput] = useState('');
  const [industryTagInput, setIndustryTagInput] = useState('');

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles([...files, ...selectedFiles]);
    setUploadSuccess(false);
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    setFiles([...files, ...droppedFiles]);
    setUploadSuccess(false);
  };

  const handleRemoveFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      setFormData({
        ...formData,
        tags: [...new Set([...formData.tags, tagInput.trim()])],
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleAddIndustryTag = (e) => {
    if (e.key === 'Enter' && industryTagInput.trim()) {
      e.preventDefault();
      setFormData({
        ...formData,
        industryTags: [...new Set([...formData.industryTags, industryTagInput.trim()])],
      });
      setIndustryTagInput('');
    }
  };

  const handleRemoveIndustryTag = (tagToRemove) => {
    setFormData({
      ...formData,
      industryTags: formData.industryTags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleSubmit = async () => {
    if (files.length === 0) return;

    setUploading(true);
    try {
      // Pass files as File objects directly
      await onSubmit({
        variables: {
          files: files,
          ...formData
        },
        context: {
          hasUpload: true
        }
      });
      setUploadSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + (error.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setUploadSuccess(false);
    setFormData({
      category: '',
      tags: [],
      applicableContexts: [],
      industryTags: [],
      expertiseLevel: 'intermediate',
      documentPurpose: 'reference',
    });
    setTagInput('');
    setIndustryTagInput('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Import RAG Documents</DialogTitle>
      <DialogContent>
        <Box className={classes.form}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Upload multiple files at once. All documents will be created as public and converted to RAG format.
          </Typography>

          <input
            accept=".pdf,.txt,.md"
            style={{ display: 'none' }}
            id="bulk-file-upload"
            type="file"
            multiple
            onChange={handleFileSelect}
          />
          <label htmlFor="bulk-file-upload">
            <Box
              className={classes.uploadArea}
              onDrop={handleFileDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <UploadIcon fontSize="large" color="primary" />
              <Typography variant="h6">Drop files here or click to browse</Typography>
              <Typography variant="caption" color="textSecondary">
                Supported formats: PDF, TXT, MD
              </Typography>
            </Box>
          </label>

          {files.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected Files ({files.length})
              </Typography>
              <List className={classes.fileList}>
                {files.map((file, index) => (
                  <ListItem key={index} dense>
                    <ListItemText
                      primary={file.name}
                      secondary={`${(file.size / 1024).toFixed(2)} KB`}
                    />
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      disabled={uploading}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select value={formData.category} onChange={handleChange('category')}>
              <MenuItem value="">None</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Document Purpose</InputLabel>
            <Select value={formData.documentPurpose} onChange={handleChange('documentPurpose')}>
              {DOCUMENT_PURPOSES.map((purpose) => (
                <MenuItem key={purpose} value={purpose}>
                  {purpose.replace('_', ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Expertise Level</InputLabel>
            <Select value={formData.expertiseLevel} onChange={handleChange('expertiseLevel')}>
              {EXPERTISE_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <TextField
              label="Add Tags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleAddTag}
              fullWidth
              helperText="Press Enter to add tags"
            />
            <Box className={classes.chipInput} mt={1}>
              {formData.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  size="small"
                />
              ))}
            </Box>
          </Box>

          <Autocomplete
            multiple
            options={COMMON_CONTEXTS}
            value={formData.applicableContexts}
            onChange={(e, value) => setFormData({ ...formData, applicableContexts: value })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Applicable Contexts"
                helperText="Select contexts where these documents are relevant"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip size="small" label={option} {...getTagProps({ index })} />
              ))
            }
          />

          <Box>
            <TextField
              label="Add Industry Tags"
              value={industryTagInput}
              onChange={(e) => setIndustryTagInput(e.target.value)}
              onKeyPress={handleAddIndustryTag}
              fullWidth
              helperText="e.g., financial_services, wealth_management"
            />
            <Box className={classes.chipInput} mt={1}>
              {formData.industryTags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleRemoveIndustryTag(tag)}
                  size="small"
                />
              ))}
            </Box>
          </Box>

          {uploadSuccess && (
            <Box className={classes.successMessage}>
              <SuccessIcon />
              <Typography>Successfully uploaded {files.length} file(s)!</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={files.length === 0 || uploading}
          startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
        >
          {uploading ? 'Uploading...' : `Upload ${files.length} File(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportDialog;
